import {
  Alerta,
  Alternador,
  Botao,
  Caixa,
  Campo,
  Cartao,
  Entrada,
  EstadoVazio,
  Etiqueta,
  Modal,
  RodapePaginacao,
  Selecao,
  TabelaDados,
  useAvisos,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { catalogosApi } from '../../api/catalogos'
import { chaves } from '../../api/chaves'
import type {
  EntradaAtualizarItemCatalogo,
  EntradaCriarItemCatalogo,
  ItemCatalogo,
  NomeCatalogo,
} from '@baiocchi/shared'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeBusca, IconeEditar, IconeMais } from '../../componentes/icones'
import { useDebounce } from '../../ganchos/usar-debounce'
import { CabecalhoPagina } from '../../layout/layout-app'
import type { Permissao } from '@baiocchi/shared'

/** Paleta oferecida no cadastro de motivo de agendamento (cor do evento na TELA-08). */
const CORES_MOTIVO = [
  { valor: '#2a78d6', rotulo: 'Azul' },
  { valor: '#eb6834', rotulo: 'Laranja' },
  { valor: '#1baf7a', rotulo: 'Verde' },
  { valor: '#eda100', rotulo: 'Amarelo' },
  { valor: '#e87ba4', rotulo: 'Rosa' },
  { valor: '#4a3aa7', rotulo: 'Violeta' },
  { valor: '#e34948', rotulo: 'Vermelho' },
  { valor: '#6b747c', rotulo: 'Cinza' },
]

export interface ConfiguracaoCatalogo {
  catalogo: NomeCatalogo
  titulo: string
  descricao: string
  permissao: Permissao
  /** TELA-12: cor + "contabilizar nos relatórios". */
  temCor?: boolean
  temContabilizar?: boolean
  /** TELA-14: código de referência externo. */
  temCodigoReferencia?: boolean
}

/**
 * Tela de catálogo — serve as TELA-12, 13, 14, 15 e o cadastro de Tags.
 *
 * Uma tela parametrizada e não cinco arquivos: a API trata os cinco recursos com o
 * **mesmo contrato** (§4), e cinco cópias divergiriam na primeira correção — que
 * neste caso é grave, porque são as telas que a operação usa para consertar dado
 * errado (o legado tem duplicatas e registros de teste em quase todos).
 *
 * **Não existe excluir.** Registro em uso nunca é apagado (RN-41): a ação é
 * desativar, e o histórico já gravado mantém o título antigo. Título duplicado entre
 * ativos volta `409`, comparando sem acento e sem diferenciar maiúsculas.
 */
export function PaginaCatalogo(configuracao: ConfiguracaoCatalogo) {
  const { catalogo, titulo, descricao, permissao } = configuracao
  const { pode } = useSessao()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()

  const [pagina, setPagina] = useState(1)
  const [limite, setLimite] = useState(50)
  const [busca, setBusca] = useState('')
  const [mostrarInativos, setMostrarInativos] = useState(false)
  const [emEdicao, setEmEdicao] = useState<ItemCatalogo | 'novo' | null>(null)

  const buscaAtrasada = useDebounce(busca)
  const podeGerenciar = pode(permissao)

  const filtros = {
    limite,
    pagina,
    ordenar: 'ordem',
    ...(buscaAtrasada ? { busca: buscaAtrasada } : {}),
    ...(mostrarInativos ? {} : { ativo: 'true' }),
  }

  const consulta = useQuery({
    queryKey: chaves.catalogos.lista(catalogo, filtros),
    queryFn: ({ signal }) => catalogosApi.listar(catalogo, filtros, signal),
    retry: false,
    staleTime: 30_000,
  })

  const alternarStatus = useMutation({
    mutationFn: (item: ItemCatalogo) =>
      item.ativo
        ? catalogosApi.desativar(catalogo, item.id)
        : catalogosApi.reativar(catalogo, item.id),
    onSuccess: (_, item) => {
      void clienteConsulta.invalidateQueries({ queryKey: chaves.catalogos.todos })
      avisos.sucesso(item.ativo ? 'Registro desativado' : 'Registro reativado')
    },
    onError: (erro) => avisos.erro('Não foi possível alterar o status', erro.message),
  })

  const colunas: ReadonlyArray<ColunaTabela<ItemCatalogo>> = [
    {
      chave: 'id',
      cabecalho: 'Id',
      classe: 'w-16 tabular-nums',
      celula: (item) => <span className="text-xs text-conteudo-fraco">{item.id}</span>,
    },
    {
      chave: 'titulo',
      cabecalho: 'Título',
      ordenavel: true,
      celula: (item) => (
        <span className="flex items-center gap-2">
          {configuracao.temCor && item.cor ? (
            <span
              aria-hidden="true"
              className="h-3 w-3 shrink-0 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: item.cor }}
            />
          ) : null}
          <span className="font-medium text-conteudo">{item.titulo}</span>
        </span>
      ),
    },
    ...(configuracao.temCodigoReferencia
      ? [
          {
            chave: 'codigoReferencia',
            cabecalho: 'Código de referência',
            ocultarAte: 'md' as const,
            celula: (item: ItemCatalogo) => (
              <span className="font-mono text-xs text-conteudo-suave">
                {item.codigoReferencia ?? '—'}
              </span>
            ),
          },
        ]
      : []),
    {
      chave: 'ordem',
      cabecalho: 'Ordem',
      alinhamento: 'centro',
      ordenavel: true,
      classe: 'w-20 tabular-nums',
      celula: (item) => <span className="text-xs">{item.ordem}</span>,
    },
    ...(configuracao.temContabilizar
      ? [
          {
            chave: 'contabilizarRelatorio',
            cabecalho: 'Em relatórios',
            alinhamento: 'centro' as const,
            ocultarAte: 'md' as const,
            celula: (item: ItemCatalogo) => (
              <Etiqueta tom={item.contabilizarRelatorio ? 'sucesso' : 'neutro'}>
                {item.contabilizarRelatorio ? 'Sim' : 'Não'}
              </Etiqueta>
            ),
          },
        ]
      : []),
    {
      chave: 'ativo',
      cabecalho: 'Status',
      alinhamento: 'centro',
      classe: 'w-28',
      celula: (item) => (
        <Alternador
          marcado={item.ativo}
          desabilitado={!podeGerenciar || alternarStatus.isPending}
          onAlterar={() => alternarStatus.mutate(item)}
          rotulo={item.ativo ? 'Ativo' : 'Inativo'}
          className="justify-center text-xs"
        />
      ),
    },
    {
      chave: 'acoes',
      cabecalho: '',
      alinhamento: 'direita',
      classe: 'w-16',
      celula: (item) => (
        <Botao
          variante="fantasma"
          tamanho="icone"
          aria-label={`Editar ${item.titulo}`}
          disabled={!podeGerenciar}
          onClick={() => setEmEdicao(item)}
        >
          <IconeEditar className="h-4 w-4" />
        </Botao>
      ),
    },
  ]

  const itens = consulta.data?.dados ?? []

  return (
    <>
      <CabecalhoPagina
        titulo={titulo}
        descricao={descricao}
        acoes={
          podeGerenciar ? (
            <Botao
              onClick={() => setEmEdicao('novo')}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Novo
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-56 flex-1">
            <Entrada
              value={busca}
              onChange={(evento) => {
                setBusca(evento.target.value)
                setPagina(1)
              }}
              placeholder="Buscar…"
              prefixo={<IconeBusca className="h-4 w-4" />}
              aria-label={`Buscar em ${titulo}`}
            />
          </div>
          <Caixa
            rotulo="Mostrar desativados"
            checked={mostrarInativos}
            onChange={(evento) => {
              setMostrarInativos(evento.target.checked)
              setPagina(1)
            }}
          />
        </div>
      </Cartao>

      {!podeGerenciar && (
        <Alerta tom="info" className="mb-4">
          Seu perfil pode consultar este catálogo — a manutenção é restrita.
        </Alerta>
      )}

      <Cartao semPadding>
        <TabelaDados
          colunas={colunas}
          itens={itens}
          chaveDe={(item) => String(item.id)}
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro
                erro={consulta.error}
                aoTentarNovamente={() => void consulta.refetch()}
              />
            ) : undefined
          }
          realce={(item) => !item.ativo}
          vazio={
            <EstadoVazio
              titulo="Nenhum registro"
              descricao="Cadastre o primeiro item para que ele apareça nos formulários de atendimento."
              acao={
                podeGerenciar ? <Botao onClick={() => setEmEdicao('novo')}>Novo</Botao> : undefined
              }
            />
          }
        />

        <RodapePaginacao
          limite={limite}
          onAlterarLimite={(novo) => {
            setLimite(novo)
            setPagina(1)
          }}
          quantidadeNaPagina={itens.length}
          indiceInicial={itens.length === 0 ? 0 : (pagina - 1) * limite + 1}
          total={consulta.data?.paginacao.total ?? null}
          pagina={pagina}
          temAnterior={pagina > 1}
          temProximo={itens.length === limite}
          onAnterior={() => setPagina((valor) => Math.max(1, valor - 1))}
          onProximo={() => setPagina((valor) => valor + 1)}
        />
      </Cartao>

      <ModalItemCatalogo
        configuracao={configuracao}
        item={emEdicao}
        onFechar={() => setEmEdicao(null)}
      />
    </>
  )
}

function ModalItemCatalogo({
  configuracao,
  item,
  onFechar,
}: {
  configuracao: ConfiguracaoCatalogo
  item: ItemCatalogo | 'novo' | null
  onFechar: () => void
}) {
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const ehNovo = item === 'novo'
  const registro = item !== 'novo' && item !== null ? item : null

  const [estado, setEstado] = useState({
    titulo: '',
    ordem: 0,
    cor: CORES_MOTIVO[0]?.valor ?? '#2a78d6',
    contabilizarRelatorio: true,
    codigoReferencia: '',
  })
  const [erro, setErro] = useState<string | null>(null)

  // Dependência é a identidade do item em edição, não seus campos: incluí-los faria
  // o efeito rodar a cada `refetch` e sobrescrever o que está sendo digitado.
  useEffect(() => {
    if (item === null) return
    setErro(null)
    setEstado({
      titulo: registro?.titulo ?? '',
      ordem: registro?.ordem ?? 0,
      cor: registro?.cor ?? CORES_MOTIVO[0]?.valor ?? '#2a78d6',
      contabilizarRelatorio: registro?.contabilizarRelatorio ?? true,
      codigoReferencia: registro?.codigoReferencia ?? '',
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item])

  /**
   * `ativo` **não** entra no corpo: desativar e reativar têm rota própria porque
   * reativar faz o título voltar a disputar a unicidade do RN-42 e pode colidir. O
   * schema compartilhado é `.strict()` e recusava o corpo inteiro com `422` — nenhum
   * dos cinco catálogos aceitava cadastro.
   *
   * As anotações de tipo são o que faz o TypeScript recusar campo a mais: excesso de
   * propriedade só é conferido em literal anotado, não em variável solta passada
   * adiante.
   */
  const salvar = useMutation({
    mutationFn: () => {
      const camposExtras = {
        ...(configuracao.temCor ? { cor: estado.cor } : {}),
        ...(configuracao.temContabilizar
          ? { contabilizarRelatorio: estado.contabilizarRelatorio }
          : {}),
        ...(configuracao.temCodigoReferencia
          ? { codigoReferencia: estado.codigoReferencia.trim() || null }
          : {}),
      }

      if (ehNovo) {
        const corpo: EntradaCriarItemCatalogo = {
          titulo: estado.titulo.trim(),
          ordem: Number(estado.ordem),
          ...camposExtras,
        }
        return catalogosApi.criar(configuracao.catalogo, corpo)
      }

      const corpo: EntradaAtualizarItemCatalogo = {
        titulo: estado.titulo.trim(),
        ordem: Number(estado.ordem),
        ...camposExtras,
      }
      return catalogosApi.atualizar(configuracao.catalogo, registro?.id as number, corpo)
    },
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: chaves.catalogos.todos })
      avisos.sucesso(ehNovo ? 'Registro criado' : 'Registro atualizado')
      onFechar()
    },
    onError: (causa) => setErro(causa instanceof Error ? causa.message : 'Não foi possível salvar.'),
  })

  return (
    <Modal
      aberto={item !== null}
      onFechar={onFechar}
      travado={salvar.isPending}
      titulo={ehNovo ? `Novo em ${configuracao.titulo}` : `Editar ${registro?.titulo ?? ''}`}
      largura="sm"
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={salvar.isPending}>
            Fechar
          </Botao>
          <Botao
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={estado.titulo.trim().length < 2}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Alerta tom="perigo">{erro}</Alerta> : null}

        <Campo rotulo="Título" obrigatorio dica="Único entre os registros ativos.">
          {(atributos) => (
            <Entrada
              {...atributos}
              autoFocus
              value={estado.titulo}
              onChange={(evento) => setEstado((atual) => ({ ...atual, titulo: evento.target.value }))}
            />
          )}
        </Campo>

        <Campo rotulo="Ordem" dica="Define a sequência nos selects e na legenda do calendário.">
          {(atributos) => (
            <Entrada
              {...atributos}
              type="number"
              min={0}
              value={String(estado.ordem)}
              onChange={(evento) =>
                setEstado((atual) => ({ ...atual, ordem: Number(evento.target.value) }))
              }
            />
          )}
        </Campo>

        {configuracao.temCor && (
          <Campo rotulo="Cor" dica="É a cor do evento na grade de agendamentos.">
            {(atributos) => (
              <div className="flex items-center gap-2">
                <Selecao
                  {...atributos}
                  value={estado.cor}
                  opcoes={CORES_MOTIVO}
                  onChange={(evento) => setEstado((atual) => ({ ...atual, cor: evento.target.value }))}
                />
                <span
                  aria-hidden="true"
                  className="h-9 w-9 shrink-0 rounded-md ring-1 ring-borda"
                  style={{ backgroundColor: estado.cor }}
                />
              </div>
            )}
          </Campo>
        )}

        {configuracao.temCodigoReferencia && (
          <Campo rotulo="Código de referência" dica="Código externo, usado por integrações.">
            {(atributos) => (
              <Entrada
                {...atributos}
                value={estado.codigoReferencia}
                onChange={(evento) =>
                  setEstado((atual) => ({ ...atual, codigoReferencia: evento.target.value }))
                }
              />
            )}
          </Campo>
        )}

        {configuracao.temContabilizar && (
          <Caixa
            rotulo="Contabilizar nos relatórios"
            dica="Desmarcado, o registro deixa de entrar nas métricas — sem apagar o histórico."
            checked={estado.contabilizarRelatorio}
            onChange={(evento) =>
              setEstado((atual) => ({ ...atual, contabilizarRelatorio: evento.target.checked }))
            }
          />
        )}

        {/*
          Sem caixa "Ativo" aqui: o status muda pelo alternador da coluna Status, que
          chama `desativar`/`reativar` — e é a reativação que reconfere a unicidade do
          título entre ativos (RN-42). Uma segunda via nesta modal contornaria isso.
        */}
      </div>
    </Modal>
  )
}
